import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Select, Upload } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import ImportReview from './ImportReview';
import { useInspectEpiImport } from '../hooks/useInspectEpiImport';
import { useMessage } from '../../../hooks/useMessage';
import {
  assignProducts,
  assignmentProblem,
  compositionsByTab,
  defaultAssignment,
  mergeOtherLanguage,
  type ImportAssignment,
} from '../utils/importOps';
import {
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from '../../../translate';
import { TAB_CONFIGS } from '../types/constants';
import type {
  EpiComposition,
  EpiDocumentType,
  EpiIdmpRef,
} from '../types/types';

/** The only thing `inspect` reads. */
const DOCX = '.docx';

const WHOLE_EPI = 'epi';

/**
 * Pre-QRD PIs come one file per composition. The BE reads any of the four,
 * but only the SmPC and the leaflet have been compared against their old
 * titles so far.
 */
const SINGLE_COMPOSITIONS: EpiDocumentType[] = ['smpc', 'packageLeaflet'];

const SCOPE_OPTIONS = [
  { value: WHOLE_EPI, label: 'The whole ePI' },
  ...TAB_CONFIGS.filter(({ key }) => SINGLE_COMPOSITIONS.includes(key)).map(
    ({ key, label }) => ({ value: key, label: `${label} only` })
  ),
];

interface IProps {
  isOpen: boolean;
  /** Which ePI model version — the catalogue is read from its schema. */
  modelVersionId: string;
  /** The ePI's products, named in the authoring language. */
  products: { id: string; name: string }[];
  productsById: Record<string, EpiIdmpRef>;
  /** The ePI's live compositions per tab — what the import merges against. */
  currentByTab: Record<string, EpiComposition[]>;
  languageLabels: Record<SupportedLanguage, string>;
  /** The language the editor is on — the import defaults to it. */
  lang: SupportedLanguage;
  onConfirm: (byTab: Record<string, EpiComposition[]>) => void;
  onCancel: () => void;
}

/**
 * Imports an authored DOCX over this ePI.
 *
 * Two steps, because the document cannot answer the only question that
 * matters: it names products as it prints them and has never heard of an IDMP,
 * so the user assigns them. Read first, decide second, and nothing is written
 * until they confirm.
 */
const ImportEpiModal: React.FC<IProps> = ({
  isOpen,
  modelVersionId,
  products,
  productsById,
  currentByTab,
  languageLabels,
  lang,
  onConfirm,
  onCancel,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [language, setLanguage] = useState<SupportedLanguage>(lang);
  const [scope, setScope] = useState<string>(WHOLE_EPI);
  const [assignment, setAssignment] = useState<ImportAssignment>({});
  const { inspectImport, isInspecting, inspected, resetInspection } =
    useInspectEpiImport();
  const message = useMessage();

  const productIds = useMemo(() => products.map(p => p.id), [products]);

  // A fresh reading means a fresh set of rows to answer for.
  useEffect(() => {
    if (inspected) {
      setAssignment(defaultAssignment(inspected.compositions, productIds));
    }
  }, [inspected, productIds]);

  // The other language is carried across where the two trees agree; what it
  // keeps and what it cannot is the one cost of importing worth reporting.
  const otherLanguage = SUPPORTED_LANGUAGES.find(l => l !== language);
  const merged = useMemo(
    () =>
      inspected && otherLanguage
        ? mergeOtherLanguage(
            compositionsByTab(inspected),
            currentByTab,
            otherLanguage
          )
        : null,
    [inspected, currentByTab, otherLanguage]
  );

  const problem = inspected
    ? assignmentProblem(inspected.compositions, assignment, productIds)
    : null;

  const close = () => {
    setFile(null);
    resetInspection();
    setAssignment({});
    onCancel();
  };

  return (
    <Modal
      open={isOpen}
      title="Import a document"
      width={720}
      okText={inspected ? 'Replace and import' : 'Read the document'}
      okButtonProps={{
        danger: !!inspected,
        disabled: inspected ? !!problem : !file,
        loading: isInspecting,
      }}
      onOk={() => {
        if (inspected && merged) {
          onConfirm(
            assignProducts(
              merged.compositions,
              inspected.compositions,
              assignment,
              productsById
            )
          );
          close();
          return;
        }
        if (file) {
          inspectImport({
            file,
            modelVersionId,
            language,
            composition:
              scope === WHOLE_EPI ? undefined : (scope as EpiDocumentType),
          });
        }
      }}
      onCancel={close}
      destroyOnHidden
    >
      {!inspected ? (
        <>
          <Field>
            <Label>Language of the document</Label>
            <Select
              value={language}
              onChange={setLanguage}
              options={SUPPORTED_LANGUAGES.map(l => ({
                value: l,
                label: languageLabels[l],
              }))}
              style={{ width: 200 }}
            />
          </Field>
          <Field>
            <Label>What the file holds</Label>
            <Select
              value={scope}
              onChange={setScope}
              options={SCOPE_OPTIONS}
              style={{ width: 200 }}
            />
          </Field>
          <Upload.Dragger
            accept={DOCX}
            maxCount={1}
            beforeUpload={selected => {
              // `accept` filters the file picker but not a drop — rc-upload
              // applies it to dropped files only when reading a directory.
              if (!selected.name.toLowerCase().endsWith(DOCX)) {
                message.error('Only a Word .docx document can be imported.');
                return Upload.LIST_IGNORE;
              }
              setFile(selected);
              return false;
            }}
            onRemove={() => setFile(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Drop the authored .docx here, or click to choose one
            </p>
          </Upload.Dragger>
          <Note>
            Reading it changes nothing. You will see what the file turned out to
            be, and what importing it would replace, before anything is written.
          </Note>
        </>
      ) : (
        <ImportReview
          result={inspected}
          fileName={file?.name ?? ''}
          compositionName={TAB_CONFIGS.find(t => t.key === scope)?.label}
          products={products}
          assignment={assignment}
          onAssign={(key, ids) =>
            setAssignment(current => ({ ...current, [key]: ids }))
          }
          kept={merged?.kept ?? 0}
          dropped={merged?.dropped ?? 0}
          lost={merged?.lost ?? []}
          skipped={merged?.skipped ?? []}
          otherLanguageName={
            otherLanguage ? languageLabels[otherLanguage] : 'other-language'
          }
          importLanguageName={languageLabels[language]}
          problem={problem}
        />
      )}
    </Modal>
  );
};

export default ImportEpiModal;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
`;

const Note = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
`;
